import React, { useEffect, useMemo, useState } from "react";
import "./search-management.scss";
import {
  createSource,
  deleteSource,
  getMediaCatalog,
  getSearchOverview,
  getSources,
  pauseSource,
  resumeSource,
  runSourceNow,
  updateSource,
} from "../../api/crawlerApi";
import { extractList } from "../../api/client";

const sourceTypes = ["rss", "search", "rss_search"];
const languages = ["English", "Chinese", "Malay", "Multi-language"];
const statuses = ["active", "inactive", "paused", "failed"];
const rateLimitPeriods = ["minute", "hour", "day"];

const parseRateLimit = (value) => {
  const match = String(value || "").trim().match(/^(\d+)\s+(?:records?|requests?)\s*\/\s*(minute|hour|day)$/i);

  return match
    ? { requests: Number(match[1]), period: match[2].toLowerCase() }
    : { requests: 20, period: "hour" };
};

const blankForm = {
  media_key: "",
  name: "",
  method: "search",
  rss_url: "",
  search_url: "",
  language: "English",
  status: "active",
  crawl_interval_hours: 6,
  rate_limit_requests: 20,
  rate_limit_period: "hour",
  keywords: "",
};

const getStatusClass = (status) =>
  `search-status search-status--${String(status || "inactive").toLowerCase()}`;

const formatCrawlDateTime = (value) => {
  if (!value) return "Never";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function SearchManagementPage() {
  const [mediaCatalog, setMediaCatalog] = useState([]);
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [crawlStates, setCrawlStates] = useState({});

  const loadData = async () => {
    try {
      const [overviewResponse, sourcesResponse, catalogResponse] = await Promise.all([
        getSearchOverview(),
        getSources(),
        getMediaCatalog(),
      ]);

      setSummary(overviewResponse.summary || {});
      setSources(extractList(sourcesResponse));
      setMediaCatalog(catalogResponse);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load search sources.");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSources = useMemo(() => {
    return sources.filter((source) => {
      const keyword = search.trim().toLowerCase();
      const matchesKeyword =
        !keyword ||
        [source.name, source.language, source.keywords]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword));

      const matchesStatus = statusFilter === "all" || source.status === statusFilter;
      const matchesType = typeFilter === "all" || source.method === typeFilter;

      return matchesKeyword && matchesStatus && matchesType;
    });
  }, [sources, search, statusFilter, typeFilter]);

  const availableMediaCatalog = useMemo(() => {
    const addedMediaKeys = new Set(
      sources
        .map((source) => source.metadata?.media_key)
        .filter(Boolean)
    );
    const addedMediaNames = new Set(
      sources
        .map((source) => String(source.name || "").trim().toLowerCase())
        .filter(Boolean)
    );

    return mediaCatalog.filter((media) => (
      !addedMediaKeys.has(media.key) &&
      !addedMediaNames.has(String(media.name || "").trim().toLowerCase())
    ));
  }, [mediaCatalog, sources]);

  const handleCreate = () => {
    setEditingSource(null);
    setForm(blankForm);
    setModalError("");
    setModalOpen(true);
  };

  const handleEdit = (source) => {
    const rateLimit = parseRateLimit(source.rate_limit);

    setEditingSource(source);
    setForm({
      media_key: source.metadata?.media_key || "",
      name: source.name || "",
      method: source.method || "search",
      rss_url: source.rss_url || "",
      search_url: source.search_url || "",
      language: source.language || "English",
      status: source.status || "active",
      crawl_interval_hours: source.crawl_interval_hours || 6,
      rate_limit_requests: rateLimit.requests,
      rate_limit_period: rateLimit.period,
      keywords: source.keywords || "",
    });
    setModalError("");
    setModalOpen(true);
  };

  const handleClose = () => {
    setEditingSource(null);
    setForm(blankForm);
    setModalError("");
    setModalOpen(false);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleMediaChange = (mediaKey) => {
    const selected = mediaCatalog.find((media) => media.key === mediaKey);
    if (!selected) {
      setForm(blankForm);
      return;
    }

    setForm((current) => ({
      ...current,
      media_key: selected.key,
      name: selected.name,
      method: selected.method || "",
      rss_url: selected.rss_url || "",
      search_url: selected.search_url || "",
      language: selected.language,
      keywords: selected.keywords || "",
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setModalError("");

    const payload = {
      ...(!editingSource ? { media_key: form.media_key } : {}),
      status: form.status,
      crawl_interval_hours: form.crawl_interval_hours,
      keywords: form.keywords,
      rate_limit: `${form.rate_limit_requests} records / ${form.rate_limit_period}`,
    };
    delete payload.rate_limit_requests;
    delete payload.rate_limit_period;

    try {
      if (editingSource) {
        await updateSource(editingSource.id, payload);
      } else {
        await createSource(payload);
      }

      handleClose();
      await loadData();
    } catch (err) {
      setModalError(err.message || "Failed to save source.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this source?")) return;

    try {
      await deleteSource(id);
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to delete source.");
    }
  };

  const handleRunNow = async (source) => {
    const id = source.id;
    setCrawlStates((current) => ({ ...current, [id]: "running" }));
    setError("");

    try {
      const result = await runSourceNow(id);
      const crawlState = Object.keys(result?.errors || {}).length > 0 ? "warning" : "completed";
      setCrawlStates((current) => ({ ...current, [id]: crawlState }));
      await loadData();
    } catch (err) {
      setCrawlStates((current) => ({ ...current, [id]: "failed" }));
      setError(err.message || "Failed to queue crawl.");
    }
  };

  const handleToggleStatus = async (source) => {
    try {
      if (source.status === "active") {
        await pauseSource(source.id);
      } else {
        await resumeSource(source.id);
      }

      await loadData();
    } catch (err) {
      setError(err.message || "Failed to update source status.");
    }
  };

  return (
    <div className="search-page">
      <div className="search-page__topbar">
        <div>
          <h2>Search Management</h2>
          <p>
            Manage fixed media RSS feeds, website discovery pages, article filtering keywords, and crawl intervals.
          </p>
        </div>

        <button className="primary-button" type="button" onClick={handleCreate}>
          + Add Source
        </button>
      </div>

      <div className="search-kpi-grid">
        <div className="search-kpi-card">
          <span>Active Sources</span>
          <strong>{summary.active_sources ?? 0}</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--green">
          <span>Records Found Today</span>
          <strong>{summary.records_found_today ?? 0}</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--blue">
          <span>Average Crawl Time</span>
          <strong>{summary.average_crawl_time_seconds ?? 0} sec</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--orange">
          <span>Failed Sources</span>
          <strong>{summary.failed_sources ?? 0}</strong>
        </div>
      </div>

      <div className="search-toolbar">
        <input
          placeholder="Search source, language, or keyword..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All Status</option>
          {statuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>

        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">All Methods</option>
          {sourceTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

      </div>

      {error && (
        <div className="search-error-alert" role="alert">
          <div className="search-error-alert__icon" aria-hidden="true">!</div>
          <div className="search-error-alert__content">
            <strong>Unable to complete the request</strong>
            <p>{error}</p>
          </div>
          <button type="button" onClick={() => setError("")} aria-label="Dismiss error">×</button>
        </div>
      )}

      <div className="search-table-card">
        <div className="search-table-wrapper">
          <table className="search-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Source</th>
                <th>Method</th>
                <th>Status</th>
                <th>Language</th>
                <th>Interval</th>
                <th>Last Crawl</th>
                <th>Records Found</th>
                <th>Record Limit</th>
                <th>RSS / Search URL</th>
              </tr>
            </thead>
            <tbody>
              {filteredSources.length === 0 ? (
                <tr>
                  <td colSpan="10">No sources found.</td>
                </tr>
              ) : (
                filteredSources.map((source) => {
                  const crawlState = crawlStates[source.id];

                  return (
                  <tr key={source.id}>
                    <td>
                      <div className="search-actions">
                        <button type="button" title="Edit" onClick={() => handleEdit(source)}>✎</button>
                        <button
                          type="button"
                          className={crawlState === "running" ? "is-running" : ""}
                          title={crawlState === "running" ? "Crawl in progress" : "Run Crawl Now"}
                          aria-label={crawlState === "running" ? `Crawling ${source.name}` : `Run crawl for ${source.name}`}
                          disabled={crawlState === "running"}
                          onClick={() => handleRunNow(source)}
                        >↻</button>
                        <button type="button" title="Activate / Pause" onClick={() => handleToggleStatus(source)}>
                          {source.status === "active" ? "Ⅱ" : "▶"}
                        </button>
                        <button type="button" title="Delete" className="danger" onClick={() => handleDelete(source.id)}>🗑</button>
                      </div>
                    </td>
                    <td>
                      <strong>{source.name}</strong>
                      <span>{source.keywords}</span>
                    </td>
                    <td>{source.method}</td>
                    <td>
                      <span className={getStatusClass(source.status)}>{source.status}</span>
                      {crawlState && (
                        <span className={`crawl-state crawl-state--${crawlState}`}>
                          {crawlState === "running" && "Crawling…"}
                          {crawlState === "completed" && "Crawl completed"}
                          {crawlState === "warning" && "Completed with warnings"}
                          {crawlState === "failed" && "Crawl failed"}
                        </span>
                      )}
                    </td>
                    <td>{source.language}</td>
                    <td>{source.crawl_interval_hours ? `${source.crawl_interval_hours} hours` : "-"}</td>
                    <td>
                      <time className="search-last-crawl" dateTime={source.last_crawled_at || undefined} title={source.last_crawled_at || "Never crawled"}>
                        {formatCrawlDateTime(source.last_crawled_at)}
                      </time>
                    </td>
                    <td>{source.records_found_today ?? 0}</td>
                    <td>{source.rate_limit || "-"}</td>
                    <td>
                      <div className="search-url-cell">
                        <small>RSS: {source.rss_url || "-"}</small>
                        <small>Search: {source.search_url || "-"}</small>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="search-modal-backdrop" role="presentation">
          <form className="search-modal" onSubmit={handleSave}>
            <div className="search-modal__header">
              <div>
                <h3>{editingSource ? "Edit Source" : "Add Source"}</h3>
                <p>Configure RSS and website content discovery, keywords, and crawl frequency.</p>
              </div>

              <button type="button" onClick={handleClose} aria-label="Close">
                ×
              </button>
            </div>

            {modalError && (
              <div className="search-modal__error" role="alert">
                <span aria-hidden="true">!</span>
                <div>
                  <strong>Source could not be saved</strong>
                  <p>{modalError}</p>
                </div>
              </div>
            )}

            <div className="search-form-grid">
              <label>
                Media
                {editingSource ? (
                  <input value={form.name} readOnly />
                ) : (
                  <select required value={form.media_key} onChange={(event) => handleMediaChange(event.target.value)}>
                    <option value="">
                      {availableMediaCatalog.length ? "Select media" : "All media have been added"}
                    </option>
                    {availableMediaCatalog.map((media) => (
                      <option key={media.key} value={media.key}>
                        {media.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>

              <label>
                Method
                <select
                  value={form.method}
                  disabled
                >
                  {sourceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </label>

              <label>
                Status
                <select
                  value={form.status}
                  onChange={(event) => handleChange("status", event.target.value)}
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>

              <label>
                Language
                <select
                  value={form.language}
                  disabled
                >
                  {languages.map((language) => (
                    <option key={language}>{language}</option>
                  ))}
                </select>
              </label>

              <label>
                Crawl Interval
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="1"
                    value={form.crawl_interval_hours}
                    onChange={(event) => handleChange("crawl_interval_hours", Number(event.target.value))}
                  />
                  <span>Hours</span>
                </div>
              </label>

              <label>
                Record Limit
                <div className="rate-limit-control">
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    required
                    value={form.rate_limit_requests}
                    onChange={(event) => handleChange("rate_limit_requests", Number(event.target.value))}
                    aria-label="Maximum records"
                  />
                  <select
                    value={form.rate_limit_period}
                    onChange={(event) => handleChange("rate_limit_period", event.target.value)}
                    aria-label="Rate limit period"
                  >
                    {rateLimitPeriods.map((period) => (
                      <option key={period} value={period}>per {period}</option>
                    ))}
                  </select>
                </div>
                <small className="field-help">Discovered article links: 1–1,000.</small>
              </label>

              <label className="search-form-grid__wide">
                RSS URL
                <input
                  value={form.rss_url}
                  readOnly
                  disabled
                  title="Managed by the media integration"
                  placeholder="https://example.com/rss.xml"
                />
              </label>

              <label className="search-form-grid__wide">
                Website Crawl URL
                <input
                  value={form.search_url}
                  readOnly
                  disabled
                  title="Managed by the media integration"
                  placeholder="https://example.com/news"
                />
              </label>

              <label className="search-form-grid__wide">
                Keywords
                <textarea
                  value={form.keywords}
                  onChange={(event) => handleChange("keywords", event.target.value)}
                  placeholder="construction, development, project, tender, contract"
                />
              </label>
            </div>

            <div className="search-modal__actions">
              <button type="button" className="secondary-button" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className="primary-button">
                {editingSource ? "Save Changes" : "Create Source"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
